import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, DollarSign, Save } from 'lucide-react';
import { formatApiError } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BetsAdminTab = () => {
  const [mds, setMds] = useState([]);
  const [mdId, setMdId] = useState('');
  const [preview, setPreview] = useState(null);
  const [players, setPlayers] = useState([]);
  const [topScorer, setTopScorer] = useState('');
  const [topAssister, setTopAssister] = useState('');
  const [totalGoals, setTotalGoals] = useState(0);
  const [perPlayer, setPerPlayer] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const loadMds = async () => {
    try {
      const { data } = await axios.get(`${API}/fantasy/admin/match-days`);
      setMds(data || []);
      if ((data || []).length > 0 && !mdId) setMdId(data[0].id);
    } catch (ex) { setErr(formatApiError(ex)); }
  };

  const loadPreview = async () => {
    if (!mdId) return;
    try {
      const [p, pl] = await Promise.all([
        axios.get(`${API}/fantasy/admin/bets/preview`, { params: { match_day_id: mdId } }),
        axios.get(`${API}/fantasy/players`),
      ]);
      setPreview(p.data);
      setPlayers(pl.data || []);
    } catch (ex) { setErr(formatApiError(ex)); }
  };

  useEffect(() => { loadMds(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (mdId) loadPreview(); /* eslint-disable-next-line */ }, [mdId]);

  const settle = async () => {
    if (!mdId) return;
    if (!window.confirm(`Processar todas as apostas desta jornada? Esta ação credita PC aos vencedores e não pode ser desfeita.`)) return;
    setBusy(true); setMsg(''); setErr('');
    try {
      const body = {
        match_day_id: mdId,
        top_scorer: topScorer || null,
        top_assister: topAssister || null,
        total_goals: totalGoals === '' ? null : Number(totalGoals),
        per_player_goals: Object.fromEntries(Object.entries(perPlayer).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])),
      };
      const { data } = await axios.post(`${API}/fantasy/admin/bets/settle`, body);
      setMsg(`✅ Processado! ${data.winners} vencedores (${data.total_paid} PC), ${data.losers} perdedores, ${data.refunded} reembolsados. Rake admin: ${data.total_rake} PC.`);
      await loadPreview();
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  const openBets = (preview?.bets || []).filter((b) => b.status === 'open');
  const settledBets = (preview?.bets || []).filter((b) => b.status !== 'open');
  const playerNames = players.map((p) => p.player_name).sort();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-300 font-medium">Jornada:</label>
        <select value={mdId} onChange={(e) => setMdId(e.target.value)}
          className="bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
          data-testid="bets-settle-md-select">
          {mds.map((m) => <option key={m.id} value={m.id}>{m.name || m.date} · {m.status}</option>)}
        </select>
        {preview && <span className="text-sm text-slate-400">Total: <span className="text-white font-bold">{preview.total_bets}</span> apostas</span>}
      </div>

      {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
      {msg && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">{msg}</div>}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-slate-900/60 border border-white/10 rounded-2xl p-4 space-y-3">
          <h3 className="text-white font-black text-sm">🏁 Resultado do domingo</h3>

          <div>
            <label className="text-xs text-slate-400 uppercase">Artilheiro do dia</label>
            <select value={topScorer} onChange={(e) => setTopScorer(e.target.value)}
              className="mt-1 w-full bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="settle-top-scorer">
              <option value="">— nenhum vencedor —</option>
              {playerNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-400 uppercase">Rei das assistências</label>
            <select value={topAssister} onChange={(e) => setTopAssister(e.target.value)}
              className="mt-1 w-full bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="settle-top-assister">
              <option value="">— nenhum vencedor —</option>
              {playerNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-400 uppercase">Total de golos da pelada</label>
            <input type="number" min={0} value={totalGoals} onChange={(e) => setTotalGoals(e.target.value)}
              className="mt-1 w-full bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm tabular-nums"
              data-testid="settle-total-goals" />
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-slate-300 hover:text-white">Golos por jogador (para o mercado +1.5)</summary>
            <div className="mt-2 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
              {playerNames.map((n) => (
                <label key={n} className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-2 py-1">
                  <span className="text-xs text-slate-300 flex-1 truncate">{n}</span>
                  <input type="number" min={0} value={perPlayer[n] ?? ''} onChange={(e) => setPerPlayer((s) => ({ ...s, [n]: e.target.value }))}
                    className="w-14 bg-slate-950/60 border border-white/10 rounded px-2 py-1 text-white text-xs tabular-nums" />
                </label>
              ))}
            </div>
          </details>

          <button onClick={settle} disabled={busy || !mdId}
            className="w-full bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-black rounded-lg py-2.5 flex items-center justify-center gap-2"
            data-testid="settle-btn">
            <Save className="w-4 h-4" /> {busy ? 'A processar...' : 'Processar apostas'}
          </button>
        </div>

        <div className="bg-slate-900/60 border border-white/10 rounded-2xl p-4">
          <h3 className="text-white font-black text-sm mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-300" /> Apostas ({preview?.total_bets ?? 0})</h3>
          <div className="text-xs text-slate-400 mb-2">Em aberto: <span className="text-sky-300 font-bold">{openBets.length}</span> · Processadas: <span className="text-emerald-300 font-bold">{settledBets.length}</span></div>
          <div className="max-h-[420px] overflow-y-auto pr-1 space-y-1.5">
            {(preview?.bets || []).map((b) => (
              <div key={b.id} className="bg-slate-800/40 border border-white/5 rounded-lg p-2 flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{b.user_name}</div>
                  <div className="text-slate-400 text-xs truncate">{b.selection_label}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-slate-300 tabular-nums text-xs">{b.amount} PC</div>
                  {b.status === 'won' && <div className="text-emerald-300 font-bold tabular-nums text-xs">+{b.payout}</div>}
                  {b.status === 'lost' && <div className="text-red-300 font-bold text-xs">Perdeu</div>}
                  {b.status === 'refunded' && <div className="text-amber-300 font-bold text-xs">Reembolso</div>}
                </div>
              </div>
            ))}
            {(preview?.bets || []).length === 0 && <p className="text-slate-500 text-sm text-center py-4">Sem apostas nesta jornada.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetsAdminTab;
